import { find } from "lodash";
import { Chip } from "primereact/chip";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Typography } from "../../../../common/components";
import { TYPOGRAPHY_TYPE } from "../../../../common/components/Typography";
import { Item } from "../../../../common/models";
import { isValidUUID } from "../../../../common/utils";
import { SpacePurpose } from "../../models";
import { useSetUpSpacesStore } from "../../store";

interface SubSpaceNamesProps {
    spacePurpose: SpacePurpose;
    isReadOnly?: boolean;
    subSpacePurposesOptions?: Item[];
    openAddNewSubSpace?: () => void;
}

const SubSpaceNames = (props: SubSpaceNamesProps) => {
    const {
        spacePurpose,
        isReadOnly = false,
        openAddNewSubSpace = () => {},
        subSpacePurposesOptions = [],
    } = props;

    const {
        removeSubSpacePurposeFromSpacePurpose,
        addSubSpacePurposeToSpacePurpose,
    } = useSetUpSpacesStore();

    const { t } = useTranslation(["common", "setUpSpaces"]);

    const filteredSubSpaces: Item[] = useMemo(
        () =>
            subSpacePurposesOptions.filter(
                (option) =>
                    !find(spacePurpose.subSpacePurposes, { name: option.label })
            ),
        [spacePurpose.subSpacePurposes, subSpacePurposesOptions]
    );

    const handleAddSubSpacePurpose = (item: Item) => {
        addSubSpacePurposeToSpacePurpose(spacePurpose, {
            id: isValidUUID(item.id as string)
                ? (item.id as string)
                : undefined,
            name: item.label,
        });
    };

    return (
        <div className="flex flex-col gap-1">
            <div className="flex flex-row flex-wrap flex-start gap-1 items-center">
                {isReadOnly && spacePurpose.subSpacePurposes.length === 0 && (
                    <Typography type={TYPOGRAPHY_TYPE.bodyExtraSmallBold}>
                        -
                    </Typography>
                )}
                {spacePurpose.subSpacePurposes.map((subSpacePurpose) => (
                    <Fragment key={subSpacePurpose.id}>
                        <Chip
                            removable={!isReadOnly}
                            onRemove={() => {
                                removeSubSpacePurposeFromSpacePurpose(
                                    spacePurpose,
                                    subSpacePurpose
                                );
                                return true;
                            }}
                            label={subSpacePurpose.name}
                        />
                    </Fragment>
                ))}
                {!isReadOnly && (
                    <div>
                        <Dropdown
                            onChange={(e) => {
                                const selectedPurpose = find(
                                    filteredSubSpaces,
                                    ["id", e.value]
                                );
                                if (!selectedPurpose) return;

                                handleAddSubSpacePurpose(selectedPurpose);
                            }}
                            options={filteredSubSpaces}
                            optionValue="id"
                            optionLabel="label"
                            //@ts-ignore
                            placeholder={
                                <div className="flex items-center gap-2 justify-center h-full px-2">
                                    <i className="pi pi-plus" />
                                    {t("common:add")}
                                </div>
                            }
                            filter
                            panelFooterTemplate={
                                <div
                                    className="flex flex-row items-center px-5 py-3 gap-2 cursor-pointer hover:bg-surface-5 transition-colors duration-200 overflow-hidden border-t"
                                    onClick={openAddNewSubSpace}
                                >
                                    <i className="pi pi-plus" />
                                    {t(
                                        "setUpSpaces:spacePurposes.addNewSubSpace"
                                    )}
                                </div>
                            }
                            pt={{
                                root: {
                                    className:
                                        "!border-0 hover:bg-surface-5 text-default h-8 my-auto",
                                },
                                input: {
                                    className: "text-default p-0",
                                },
                                panel: {
                                    className: "overflow-hidden",
                                },
                                trigger: {
                                    className: "hidden",
                                },
                            }}
                            resetFilterOnHide
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubSpaceNames;
